import React, { useEffect, useMemo, useRef, useState } from "react";
import { GENDERS, buildCerebroCompetitionBody } from "../../../ForBusiness/Cacheshare/taxonomy";
import { searchLeagues } from "./coachOnboardingServices";
import { SearchField } from "./coachOnboardingPieces";

// Frame 07's League field: one search box over the leagues for the picked
// tier, region, gender and sport (CACHE partner programs first for HS), plus
// every competition in Cerebro's catalogue once two letters are typed.
// Coming leagues show a SOON tag and cannot be picked.

const LIST_ID = "coach-league-options";
const SEARCH_DELAY_MS = 250;
const GENDER_NAME = Object.fromEntries(GENDERS.map((g) => [g.value, g.name]));

const catalogueMeta = (entry = {}) => [entry.level, GENDER_NAME[entry.gender]].filter(Boolean).join(" · ");

// On phones (below 640px) the list sits at z-20: over the step's sticky
// footer (z-10, onboarding.css) and under the sticky stack header (z-30), so
// it never covers the header while the page scrolls. The clear button gets a
// 40px tap target there. Tablets and desktops keep z-30 and the bare glyph.
const LIST_CLASS = "absolute left-0 right-0 top-[60px] z-30 max-sm:z-20 max-h-[320px] overflow-y-auto rounded-[12px] border border-bm-border bg-white py-1 shadow-[0px_8px_24px_rgba(0,0,0,0.08)]";
const CLEAR_CLASS = "shrink-0 bm-body-l text-bm-tertiary hover:text-bm-ink max-sm:-mr-[10px] max-sm:flex max-sm:h-10 max-sm:w-10 max-sm:items-center max-sm:justify-center";

// The box shows the picked league's name; only this box changes the pick.
const LeagueCombobox = ({ options, gender, value, onPick }) => {
  const [query, setQuery] = useState(value?.name || "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  const q = query.trim();
  const typed = !!q && q !== value?.name;
  useEffect(() => {
    if (!typed || q.length < 2) {
      setHits([]);
      setSearching(false);
      return undefined;
    }
    let live = true;
    setSearching(true);
    const timer = setTimeout(() => {
      searchLeagues({ q, gender, limit: 12 })
        .then((rows) => live && setHits(rows || []))
        .catch(() => live && setHits([]))
        .finally(() => live && setSearching(false));
    }, SEARCH_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [q, typed, gender]);

  const rows = useMemo(() => {
    const needle = typed ? q.toLowerCase() : "";
    const listed = options.filter((b) => !needle || b.name.toLowerCase().includes(needle)).map((body) => ({ body, meta: "" }));
    const seen = new Set(listed.map((r) => r.body.value));
    const found = hits
      .map(buildCerebroCompetitionBody)
      .filter((b) => !seen.has(b.value))
      .map((body) => ({ body, meta: catalogueMeta(body.catalogue) }));
    return [...listed, ...found];
  }, [options, hits, q, typed]);
  const pickable = (i) => rows[i]?.body.status === "live";

  const choose = (body) => {
    setOpen(false);
    setActive(-1);
    setQuery(body?.name || "");
    onPick(body);
  };
  const edit = (text) => {
    setQuery(text);
    setOpen(true);
    setActive(-1);
    // Typing over a picked league unpicks it.
    if (value) onPick(null);
  };
  const close = () => {
    setOpen(false);
    setActive(-1);
    setQuery(value?.name || "");
  };
  const move = (step) => {
    if (!rows.length) return;
    let i = active;
    for (let n = 0; n < rows.length; n += 1) {
      i = (i + step + rows.length) % rows.length;
      if (pickable(i)) break;
    }
    setOpen(true);
    setActive(pickable(i) ? i : -1);
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      move(e.key === "ArrowDown" ? 1 : -1);
    } else if (e.key === "Enter" && open && pickable(active)) {
      e.preventDefault();
      choose(rows[active].body);
    } else if (e.key === "Escape") {
      close();
    }
  };

  return (
    <div className="relative">
      <SearchField
        inputRef={inputRef}
        role="combobox"
        aria-label="League"
        aria-expanded={open}
        aria-controls={LIST_ID}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${LIST_ID}-${active}` : undefined}
        placeholder="Search leagues"
        value={query}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onChange={(e) => edit(e.target.value)}
        onKeyDown={onKeyDown}
      >
        {query && (
          <button
            type="button"
            aria-label="Clear league"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              choose(null);
              inputRef.current?.focus();
            }}
            className={CLEAR_CLASS}
          >
            ×
          </button>
        )}
      </SearchField>
      {open && (
        <ul id={LIST_ID} role="listbox" aria-label="Leagues" className={LIST_CLASS}>
          {rows.map(({ body, meta }, i) => {
            const soon = body.status !== "live";
            return (
              <li
                key={body.value}
                id={`${LIST_ID}-${i}`}
                role="option"
                aria-selected={value?.value === body.value}
                aria-disabled={soon}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => !soon && choose(body)}
                className={`flex items-center justify-between gap-3 px-[18px] py-[10px] ${soon ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-bm-subtle"} ${i === active ? "bg-bm-subtle" : ""}`}
              >
                <span className="min-w-0">
                  <span className="block truncate bm-body-l text-bm-ink">{body.name}</span>
                  {meta && <span className="block bm-body-s text-bm-muted">{meta}</span>}
                </span>
                {soon && <span className="shrink-0 bm-label-xs text-bm-tertiary">SOON</span>}
              </li>
            );
          })}
          {searching && <li className="px-[18px] py-[10px] bm-body-m text-bm-muted">Searching leagues...</li>}
          {!searching && !rows.length && <li className="px-[18px] py-[10px] bm-body-m text-bm-muted">No league matches. Skip to search teams by name.</li>}
        </ul>
      )}
    </div>
  );
};

export default LeagueCombobox;
